import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface DashboardStatCardProps {
  icon: ReactNode;
  /** Tailwind background classes for the icon circle (e.g. `bg-blue-100 dark:bg-blue-900`) */
  iconBg: string;
  /** Tailwind text/icon color classes for the icon (e.g. `text-blue-600 dark:text-blue-400`) */
  iconColor: string;
  label: string;
  mobileLabel?: string;
  value: ReactNode;
  /** `compact` — denser tile for side-by-side dashboard overview columns */
  size?: 'default' | 'compact';
}

/**
 * Compact stat tile for dashboard list pages (matches academy owner Business Overview sizing).
 */
export function DashboardStatCard({
  icon,
  iconBg,
  iconColor,
  label,
  mobileLabel,
  value,
  size = 'default',
}: DashboardStatCardProps) {
  const compact = size === 'compact';

  return (
    <Card className="h-full card-hover">
      <CardContent
        className={cn(
          'h-full',
          compact ? 'p-2.5 pt-2.5 sm:p-3 sm:pt-3' : 'p-3 pt-3 sm:p-4 sm:pt-4'
        )}
      >
        <div className="flex min-w-0 items-center">
          <div
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full',
              compact
                ? 'h-7 w-7 sm:h-8 sm:w-8 [&_svg]:h-3.5 [&_svg]:w-3.5 sm:[&_svg]:h-4 sm:[&_svg]:w-4'
                : 'h-8 w-8 sm:h-10 sm:w-10 [&_svg]:h-4 [&_svg]:w-4 sm:[&_svg]:h-5 sm:[&_svg]:w-5',
              iconBg,
              iconColor
            )}
          >
            {icon}
          </div>
          <div className={cn('min-w-0', compact ? 'ml-2 sm:ml-2.5' : 'ml-2.5 sm:ml-4')}>
            <p
              className={cn(
                'text-xs font-medium leading-tight text-gray-600 dark:text-gray-400',
                !compact && 'sm:text-sm'
              )}
            >
              {mobileLabel != null ? (
                <>
                  <span className="sm:hidden">{mobileLabel}</span>
                  <span className="hidden sm:inline">{label}</span>
                </>
              ) : (
                label
              )}
            </p>
            <div
              className={cn(
                'mt-0.5 font-semibold leading-tight text-gray-900 dark:text-white',
                compact ? 'text-base sm:text-lg' : 'text-lg sm:text-2xl'
              )}
            >
              {value}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
